import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { BlockedLabels } from '@agion/ui'

/**
 * The texts of a delegation the mission setup blocked (agion/Agion#2370), one
 * key set for the conversation and the Activity sidebar: the status label, the
 * sentence "{agent} could not finish: {phrase}.", one fixed phrase per refusal
 * code, and who can change the setup. The design system picks the phrase from
 * the refusal code, never from the runner's error text.
 */
export function useBlockedOutcomeLabels(): BlockedLabels {
  const { t } = useTranslation(['chat'])
  return useMemo<BlockedLabels>(
    () => ({
      blocked: t('chat:delegation.blocked.label', 'Blocked by setup'),
      blockedMessage: (agent: string, reason: string) =>
        t('chat:delegation.blocked.message', {
          agent,
          reason,
          defaultValue: '{{agent}} could not finish: {{reason}}.',
        }),
      blockedTargetNotBound: t(
        'chat:delegation.blocked.targetNotBound',
        'the system is not connected to this mission',
      ),
      blockedTargetNotAuthorized: t(
        'chat:delegation.blocked.targetNotAuthorized',
        'this mission is not authorized to use the system',
      ),
      blockedOperationOutsideScope: t(
        'chat:delegation.blocked.operationOutsideScope',
        'the operation is not allowed in this mission',
      ),
      blockedCredentialUnavailable: t(
        'chat:delegation.blocked.credentialUnavailable',
        'the connection has no usable credentials',
      ),
      blockedOther: t(
        'chat:delegation.blocked.other',
        'the mission setup does not allow this request',
      ),
      blockedHint: t(
        'chat:delegation.blocked.hint',
        'The mission author or an administrator can change this.',
      ),
    }),
    [t],
  )
}
